/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import type { EuiSelectableOption } from '@elastic/eui'
import {
  EuiFilterButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiPopover,
  EuiPopoverTitle,
  EuiSelectable,
} from '@elastic/eui'

import { getPrettyProjectType } from '@modules/project-lib/display'
import genericMessages from '@modules/project-lib/genericMessages'
import type { ProjectType } from '@modules/ui-types/projects'
import { useQueryStringParams } from '@modules/utils/qs'

import { filterSchema, PROJECT_TYPES } from './schema'

type Props = {
  isOpen: boolean
  onClose: () => void
  onToggle: () => void
}

const ProjectTypeFilter: React.FunctionComponent<Props> = ({ isOpen, onClose, onToggle }) => {
  const { formatMessage } = useIntl()

  const {
    setQueryStringItem,
    queryStringItems: { type },
  } = useQueryStringParams(filterSchema.parse)

  const options: EuiSelectableOption[] = PROJECT_TYPES.map((projectType) => ({
    key: projectType,
    label: projectType,
    checked: projectType === type ? 'on' : undefined,
  }))

  const numFilters = options.filter((option) => option.checked === 'on').length

  const button = (
    <EuiFilterButton
      iconType='arrowDown'
      onClick={onToggle}
      isSelected={isOpen}
      hasActiveFilters={numFilters > 0}
      numActiveFilters={numFilters}
    >
      <FormattedMessage {...genericMessages.type} />
    </EuiFilterButton>
  )

  const renderOption = ({ key }: EuiSelectableOption) => {
    const projectType = key as ProjectType // explicit cast to work around shortcomings in EUI typing

    const iconType = (
      {
        elasticsearch: 'logoElasticsearch',
        observability: 'logoObservability',
        security: 'logoSecurity',
      } as const
    )[projectType]

    if (!iconType) {
      return null // sanity
    }

    return (
      <EuiFlexGroup gutterSize='m' justifyContent='flexStart' alignItems='center'>
        <EuiFlexItem grow={false}>
          <EuiIcon type={iconType} size='m' />
        </EuiFlexItem>
        <EuiFlexItem grow={true}>
          <FormattedMessage {...getPrettyProjectType(projectType)} />
        </EuiFlexItem>
      </EuiFlexGroup>
    )
  }

  return (
    <EuiPopover
      id={'type'}
      button={button}
      isOpen={isOpen}
      closePopover={onClose}
      panelPaddingSize='none'
    >
      <EuiSelectable
        aria-label={formatMessage(genericMessages.solutions)}
        options={options}
        singleSelection={true}
        onChange={(newOptions) => {
          const newType = newOptions.find((x) => x.checked)?.key
          setQueryStringItem('type', newType)
          onClose() // no need to keep open since it's single-choice
        }}
        renderOption={renderOption}
      >
        {(list, search) => (
          <div style={{ width: '16rem' }}>
            <EuiPopoverTitle paddingSize='s'>{search}</EuiPopoverTitle>
            {list}
          </div>
        )}
      </EuiSelectable>
    </EuiPopover>
  )
}

export default ProjectTypeFilter
